import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Per-tab workspace override — sessionStorage wins over localStorage so a user
// can open the same app in two windows, each pointing at a different workspace.
// See AuthContext for how the URL `?ws=<owner_id>` param seeds sessionStorage
// on mount.
export function getWorkspaceId() {
  return sessionStorage.getItem("workspace_id") || localStorage.getItem("workspace_id");
}
export function setWorkspaceId(id, { persistGlobally = true } = {}) {
  if (id) {
    sessionStorage.setItem("workspace_id", id);
    if (persistGlobally) localStorage.setItem("workspace_id", id);
  } else {
    sessionStorage.removeItem("workspace_id");
    if (persistGlobally) localStorage.removeItem("workspace_id");
  }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const ws = getWorkspaceId();
  if (ws) config.headers["X-Workspace"] = ws;
  const mode = localStorage.getItem("app_mode") || "live";
  config.headers["X-Mode"] = mode;
  return config;
});

// Auto-logout on 401 so tokens invalidated by "signed in on another device"
// or expired by the 2-hour access-token TTL immediately kick the user out.
// The login endpoints themselves ARE allowed to 401 (bad password / bad TOTP)
// without triggering the wipe — those responses come from /auth/* paths.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/verify-2fa-setup");
    if (status === 401 && !isAuthEndpoint) {
      const detail = err?.response?.data?.detail || "";
      // Wipe local session so ProtectedRoute redirects to /login on next render.
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("workspace_id");
      // Only reload if we're not already on the login page, to avoid loops.
      if (typeof window !== "undefined" && !/\/login/.test(window.location.pathname)) {
        // Small delay so any in-flight UI can show its own error state briefly.
        setTimeout(() => {
          const reason = encodeURIComponent(detail || "Your session has ended. Please sign in again.");
          window.location.href = `/login?reason=${reason}`;
        }, 50);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function num(n) {
  // Round to whole rupees everywhere. Accounting UI stayed cleaner at 26px
  // font size without trailing ".00" noise. Sub-rupee residues from PDF
  // backfills / interest still exist in the DB but are hidden from display.
  const v = Number(n) || 0;
  return Math.round(v).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Spreadsheet-style formula evaluator for the amount field.
 *   evalFormula("=236*4")        → 944
 *   evalFormula("=-234*4")       → -936
 *   evalFormula("=100+50*2")     → 200
 *   evalFormula("=(100+50)*2")   → 300
 *   evalFormula("236")           → 236   (plain numbers pass through)
 *   evalFormula("=foo()")        → null  (invalid)
 *
 * Safe: only `0-9 . + - * / ( )` and whitespace are allowed. Any other char
 * aborts and returns null — so Function() can't be tricked into executing
 * anything beyond basic arithmetic.
 */
export function evalFormula(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (!s.startsWith("=")) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  const expr = s.slice(1).trim();
  if (!/^[-+/*().0-9\s]+$/.test(expr)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict"; return (${expr});`)();
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}
